import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../button-reusable/button-reusable';
import { IconComponent } from '../icons/icon-reusable';
import {
  arrowclockwise,
  arrowcounterclockwise,
  cloudupload,
  minusIcon,
  plusIcon,
} from '../icons/icon';
import { ToastService } from '../../../service/toast.service';

@Component({
  selector: 'app-edit-profile-picture',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, IconComponent],
  templateUrl: './profile-setting.html',
})
export class EditProfilePictureComponent implements OnChanges {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly toastService = inject(ToastService);
  private readonly maxFileSizeBytes = 5 * 1024 * 1024;
  protected readonly cloudupload = cloudupload;
  protected readonly arrowclockwise = arrowclockwise;
  protected readonly arrowcounterclockwise = arrowcounterclockwise;
  protected readonly plusIcon = plusIcon;
  protected readonly minusIcon = minusIcon;

  @Input() visible = false;
  @Input() currentImage: string | null = null;
  @Input() saving = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveImage = new EventEmitter<File>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  image = '';
  zoom = 1;
  rotation = 0;

  position = { x: 0, y: 0 };
  dragging = false;
  dragStart = { x: 0, y: 0 };
  selectedFile: File | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentImage']) {
      this.image = this.currentImage ?? '';

      this.zoom = 1;
      this.rotation = 0;
      this.position = { x: 0, y: 0 };
      this.selectedFile = null;
    }
  }

  openFilePicker() {
    this.fileInput?.nativeElement.click();
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.isAllowedFileSize(file)) {
      this.selectedFile = null;
      input.value = '';
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const nextImage = typeof e.target?.result === 'string' ? e.target.result : '';

      this.ngZone.run(() => {
        this.image = nextImage;
        this.changeDetectorRef.detectChanges();
      });
    };
    reader.readAsDataURL(file);

    input.value = '';
  }
  startDrag(event: MouseEvent) {
    this.dragging = true;
    this.dragStart = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y,
    };
  }

  onDrag(event: MouseEvent) {
    if (!this.dragging) return;

    this.position = {
      x: event.clientX - this.dragStart.x,
      y: event.clientY - this.dragStart.y,
    };
  }

  stopDrag() {
    this.dragging = false;
  }

  rotateLeft() {
    this.rotation -= 90;
  }

  rotateRight() {
    this.rotation += 90;
  }

  zoomIn() {
    this.zoom = Math.min(3, this.zoom + 0.1);
  }

  zoomOut() {
    this.zoom = Math.max(0.5, this.zoom - 0.1);
  }

  save() {
    if (!this.image) return;

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = this.image;

    img.onload = () => {
      const size = canvas.width;

      ctx.clearRect(0, 0, size, size);
      ctx.save();

      ctx.translate(size / 2, size / 2);
      ctx.translate(this.position.x, this.position.y);
      ctx.rotate((this.rotation * Math.PI) / 180);
      ctx.scale(this.zoom, this.zoom);

      const scale = Math.min(size / img.width, size / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;

      ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

      ctx.restore();
      const fileName = this.selectedFile?.name || 'profile-image.png';
      const fileType = this.selectedFile?.type || 'image/png';

      canvas.toBlob((blob) => {
        if (!blob) return;

        const editedFile = new File([blob], fileName, {
          type: fileType,
        });

        if (!this.isAllowedFileSize(editedFile)) {
          return;
        }

        this.saveImage.emit(editedFile);
      }, fileType);
    };
  }

  close() {
    this.visibleChange.emit(false);
  }

  private isAllowedFileSize(file: File): boolean {
    if (file.size <= this.maxFileSizeBytes) {
      return true;
    }

    this.toastService.show('Profile image must be 5 MB or smaller.', 'error');
    return false;
  }
}
